import {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { recoverValuesFromSearchParams } from '../Crud/useFilter';
import { ListConfig, ListOptions } from './infinity-list/infinityList.types';
import { Fetcher, FetcherResponse } from './useList.types';

type QueryFetchReturn<T> = FetcherResponse<T> & {
  page: number;
};

interface UseInfinityListQueryProps<T> {
  fetcher: Fetcher<T>;

  queryKey: QueryKey;

  config?: Omit<ListConfig, 'onAfterFetchPage' | 'onAfterFetchMore'> & {
    onAfterFetchPage?: ((page: T[]) => void) | undefined;
    onAfterFetchMore?: ((page: T[]) => void) | undefined;
  };

  listOptions?: ListOptions;
  queryOptions?: Partial<
    UseInfiniteQueryOptions<
      QueryFetchReturn<T>,
      unknown,
      InfiniteData<QueryFetchReturn<T>>,
      QueryKey,
      number
    >
  >;
  useUrlParams?: boolean;
}

interface DataType<T> {
  pages: QueryFetchReturn<T>[];
  pageParams: unknown[];
  list: T[];
  count: number;
}

const STALE_TIME = 3 * 60 * 1000;

const DEFAULT_ITEMS_PER_PAGE = 10;

function validateFetchNextPage<T>(
  cachedData: QueryFetchReturn<T>,
  itemsPerPage: number,
): number | undefined {
  const isSuccess = cachedData?.tag === 'success';
  const hasNextPage = isSuccess && itemsPerPage * (cachedData.page + 1) < cachedData?.count;

  return hasNextPage ? cachedData.page + 1 : undefined;
}

/**
 * @deprecated use useInfiniteDataQuery
 */
const useInfinityListQuery = <T>({
  queryKey,
  config = {
    shouldFetch: true,
    onAfterFetchPage: undefined,
    onAfterFetchMore: undefined,
    searchParamsFormatter: undefined,
  },
  fetcher,
  listOptions,
  queryOptions,
  useUrlParams = true,
}: UseInfinityListQueryProps<T>) => {
  const location = useLocation();

  const itemsPerPage = listOptions?.itemsPerPage || DEFAULT_ITEMS_PER_PAGE;

  const params = useUrlParams
    ? recoverValuesFromSearchParams(location.search, config?.searchParamsFormatter)
    : {};

  const fetchCallback = (pageParam: number, responseData: T[]) => {
    if (config.onAfterFetchPage) {
      config.onAfterFetchPage(responseData);
    }

    if (pageParam !== 0 && config.onAfterFetchMore) {
      config.onAfterFetchMore(responseData);
    }
  };

  const fetchPages = async ({ pageParam }: { pageParam: number }) => {
    if (!config?.shouldFetch) {
      return {} as QueryFetchReturn<T>;
    }

    const response = await fetcher({
      count: itemsPerPage,
      page: pageParam,
      params: { ...listOptions, ...params },
    });

    if (response.tag === 'success') {
      fetchCallback(pageParam, response.data);
    }

    return { ...response, page: pageParam };
  };

  const {
    data: queryData,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    status,
    refetch,
  } = useInfiniteQuery<
    QueryFetchReturn<T>,
    unknown,
    InfiniteData<QueryFetchReturn<T>>,
    QueryKey,
    number
  >({
    queryKey,
    queryFn: fetchPages,
    initialPageParam: 0,
    getNextPageParam: (cachedData) => validateFetchNextPage<T>(cachedData, itemsPerPage),
    refetchOnMount: false,
    staleTime: STALE_TIME,
    ...(queryOptions as object),
  });

  const data: DataType<T> = useMemo(() => {
    if (queryData) {
      return {
        pages: queryData.pages,
        pageParams: queryData.pageParams,
        list: queryData.pages
          .flatMap((page) => (page.tag === 'success' ? page.data : []))
          .filter((v): v is T => v != null),
        count: queryData.pages[0]?.tag === 'success' ? queryData.pages[0]?.count : 0,
      };
    }

    return {
      pages: [],
      pageParams: [],
      list: [],
      count: 0,
    };
  }, [queryData]);

  const fetchMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  return {
    data,
    error,
    fetchMore,
    hasNextPage,
    isFetching,
    status,
    refetch,
  };
};

export default useInfinityListQuery;
